<template>
  <div class="channel-filters">
    <channels-search-input ref="searchInput" class="sidebar-input" @search-channels="emitSearchEvent" variant="grey"/>
    <template v-for="(values, propName, index) in $props.filterGroups">
      <channels-sidebar-category-expand v-if="values && values.length"
                                        :key="index"
                                        :category-title="$t(`tvChannels.filterTitles.${propName}`)">
        <template v-if="dataTypes[propName] === 'radio'">
          <base-radio v-for="(item, idx) in values"
                      :key="item.value + idx"
                      :label="item.value"
                      v-model="simplifiedSelectedFilters[propName]"
                      @input="handlePackageInput"
                      @mouseup.native="handlePackageClick(item.value)"
                      :value="item.value"
                      :name="propName.toString()"
                      class="filter-item"/>
        </template>
        <template v-else>
          <base-checkbox v-for="(item, idx) in values"
                         :key="item.value + idx"
                         :label="item.value || item.name"
                         v-model="simplifiedSelectedFilters[item.value]"
                         :true-value="item.value"
                         :name="item.value.toString()"
                         @input="handleCheckboxInput($event, propName, item.value)"
                         :class="propName === 'addons' ? 'filter-item' : 'filter-half-size'"/>
        </template>
      </channels-sidebar-category-expand>
    </template>
  </div>
</template>

<script>

import ChannelsSearchInput from './channels-search-input'
import ChannelsSidebarCategoryExpand from './channels-sidebar-category-expand'
import BaseRadio from '../base/base-radio'
import BaseCheckbox from '../base/base-checkbox'
import { clearQueryParameters, getQueryParameters, setQueryParameters } from '../../utils/tvChannelsUrlFactory'

export default {
  name: 'channels-sidebar-filters',
  components: { ChannelsSidebarCategoryExpand, ChannelsSearchInput, BaseRadio, BaseCheckbox },
  data () {
    return {
      dataTypes: Object.freeze({
        packages: 'radio',
        default: 'checkbox'
      }),
      selectedFilters: {},
      // potrebno zbog v-modela checkboxova - kada je deep-nestovan objekat u pitanju
      // postoje teskoce za pracenje state-a, posto Vue ne moze da bude reaktivan za takve objekte
      simplifiedSelectedFilters: {}
    }
  },
  props: {
    filterGroups: {
      type: Object,
      default: () => ({})
    }
  },
  methods: {
    emitSearchEvent (e) {
      this.$emit('search-channels', e)
    },
    resetFilters () {
      this.selectedFilters = {}
      this.simplifiedSelectedFilters = {}
      this.prepareSelectedFiltersObject()
      this.selectFirstBoxPackage()
      clearQueryParameters(['platform'])
      setTimeout(() => {
        this.$emit('filter-selected', this.selectedFilters)
        this.addFiltersToUrl()
      }, 0)
    },
    clearSearch () {
      this.$refs.searchInput.clearSearch()
    },
    handlePackageInput (e) {
      this.$set(this.selectedFilters, 'packages', e)
      this.$nextTick(() => {
        this.emitFilterSelected()
      })
    },
    emitFilterSelected () {
      this.$emit('filter-selected', this.selectedFilters)
      this.addFiltersToUrl()
    },
    handlePackageClick (value) {
      if (this.simplifiedSelectedFilters.packages === value) { // same radio button clicked
        setTimeout(() => {
          this.$set(this.simplifiedSelectedFilters, 'packages', '')
          this.$set(this.selectedFilters, 'packages', '')
          this.emitFilterSelected()
          this.$forceUpdate()
        }, 50)
      }
    },
    handleCheckboxInput (state, propName, itemName) {
      if (!state) {
        // ako ne ocistimo iz objekta selectedFilters, watcher se nece pokrenuti
        this.$delete(this.selectedFilters[propName], itemName)
      } else {
        this.$set(this.selectedFilters[propName], itemName, state)
      }
      this.$emit('filter-selected', this.selectedFilters)
      // add filters
      this.addFiltersToUrl()
    },
    addFiltersToUrl () {
      const filterObj = {}
      Object.entries(this.selectedFilters).forEach(filter => {
        const { 0: key, 1: value } = filter
        if (key === 'packages' || key === 'platform') {
          filterObj[key] = value
        } else {
          if (!filterObj[key]) filterObj[key] = []
          filterObj[key] = [...Object.keys(value)]
        }
      })
      setQueryParameters(filterObj)
    },
    selectFirstBoxPackage () {
      const boxPackage = this.$props.filterGroups.packages.find(pack => pack.value.indexOf('Box') > -1)
      if (boxPackage && Object.keys(boxPackage).length) {
        this.$set(this.selectedFilters, 'packages', boxPackage.value)
        this.$set(this.simplifiedSelectedFilters, 'packages', boxPackage.value)
      } else {
        this.$set(this.selectedFilters, 'packages', this.$props.filterGroups.packages[0].value)
        this.$set(this.simplifiedSelectedFilters, 'packages', this.$props.filterGroups.packages[0].value)
      }
    },
    getFirstAvailablePackage () {
      const boxPackage = this.$props.filterGroups.packages.find(pack => pack.value.indexOf('Box') > -1)
      const firstPackage = this.$props.filterGroups.packages[0].value
      return (boxPackage && boxPackage.value) || firstPackage
    },
    selectFilters (filterData) {
      Object.entries(filterData).forEach(filter => {
        const { 0: key, 1: value } = filter
        if (key === 'packages' || key === 'platform') {
          this.selectedFilters[key] = value
          this.simplifiedSelectedFilters[key] = value
        } else {
          if (value) {
            const arrFromValue = value.split('|')
            arrFromValue.forEach(val => {
              if (!this.selectedFilters[key]) this.selectedFilters[key] = {}
              this.$set(this.selectedFilters[key], val, true)
              // podesavamo tzv simplifikovane filtere, da bi checkbox i ostalo bilo oznaceno
              this.$set(this.simplifiedSelectedFilters, val, true)
            })
          }
        }
      })
    },
    prepareSelectedFiltersObject () {
      Object.keys(this.$props.filterGroups).forEach(group => {
        if (group === 'packages') {
          this.selectedFilters.packages = ''
        } else {
          this.selectedFilters[group] = {}
        }
      })
    }
  },
  watch: {
    filterGroups: {
      handler (newVal) {
        if (newVal && Object.keys(newVal).length) {
          this.prepareSelectedFiltersObject()
          const queryParams = getQueryParameters()
          if (Object.keys(queryParams).length) {
            if (queryParams.packages && this.$props.filterGroups.packages.some(pack => pack.value === queryParams.packages)) {
              this.selectedFilters.packages = queryParams.packages
              this.simplifiedSelectedFilters.packages = queryParams.packages
            } else {
              queryParams.packages = this.getFirstAvailablePackage()
              this.selectFirstBoxPackage()
            }
          } else {
            queryParams.packages = this.getFirstAvailablePackage()
            this.selectFirstBoxPackage()
          }
          this.selectFilters(queryParams)
          this.addFiltersToUrl()
          this.$emit('filter-selected', this.selectedFilters)
        }
      },
      immediate: true
    }
  }
}
</script>

<style scoped lang="scss">
.channel-filters {
  background-color: $white;
  border-radius: 0.5rem;
  padding: 2.4rem 1.6rem;
  width: 100%;
  height: calc(100vh - 54px);
  overflow-y: auto;

  @media (map_get($breakpoints, md)) {
    height: auto;
    overflow-y: visible;
  }

  .filter-item, .filter-half-size {
    margin: 1.6rem 0 0.8rem;
    color: $text;
  }

  .filter-item {
    width: 100%;
  }

  .filter-half-size {
    width: 50%;
  }
}

.sidebar-input {
  display: none;

  @media (map_get($breakpoints, md)) {
    display: block;
  }

  /deep/ .base-input .input-container input {
    height: 4rem;
  }

  /deep/ .base-input .input-container .input-button {
    top: 0.2rem;
  }
}
</style>
